import { map, sort } from "ramda";
import { type FC, useEffect, useState } from "react";
import { CardSeparator, LoadingSpinner, VStack } from "widgets";
import { sortAlpha } from "widgets/utils";

import { useError } from "@/app-utils/graphql/useError.ts";
import { ProjectsSelect } from "@/modules/projects/components/ProjectsSelect.tsx";
import { TagListItem } from "@/modules/projects/components/TagListItem";
import { useGetTagsLazyQuery } from "@/modules/projects/graphql/tags.gql.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export interface TagManagementProps {
  /**
   * Optional. If not specified the management form will show a project select
   */
  projectId?: number;
}

export const TagManagement: FC<TagManagementProps> = ({ projectId, ...props }) => {
  const [selectedProject, setSelectedProject] = useState<number>(projectId ?? null);
  const [fetchTags, { data, error }] = useGetTagsLazyQuery({ variables: { projectId: selectedProject } });
  const tags = data?.itemTags ? sort((a, b) => sortAlpha(a.name, b.name), data.itemTags) : null;

  void useError(error);

  useEffect(() => {
    if (selectedProject) {
      void fetchTags();
    }
  }, [selectedProject]);

  return (
    <>
      {!projectId && (
        <>
          <ProjectsSelect
            label={"Select a project"}
            isRequired
            initialSelectedProjectId={projectId}
            onSelectionChange={(project) => {
              if (project) {
                setSelectedProject(project.id);
              }
            }}
          />
          <CardSeparator />
        </>
      )}
      <VStack>
        {!tags ? (
          <LoadingSpinner />
        ) : tags?.length > 0 ? (
          map(
            (tag) => (
              <TagListItem tag={tag} selectedProjectId={tag.project.id} key={tag.id} fetchCallback={fetchTags} />
            ),
            tags,
          )
        ) : (
          <ContentPlaceholder children={"No tags found"} mini />
        )}
        {/* Use additional margin on separator because we do not want a rowGap of 15 between the tags as we have in normal cards */}
        {selectedProject && (
          <>
            <CardSeparator style={{ margin: "10px 0" }} />
            <TagListItem key={`create-tag`} selectedProjectId={selectedProject} fetchCallback={fetchTags} />
          </>
        )}
      </VStack>
    </>
  );
};
